import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  ShieldCheck,
  TrendingUp,
  AlertCircle,
  Activity,
  Plus,
  Search,
  Building,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  X,
  CreditCard,
  Briefcase,
  FileText
} from 'lucide-react'; // Icons
import adminApiClient from '../api/client'; // ✅ Use admin-specific client
import { CompanyDto, ProductSummaryDto, AuditLogDto } from '../types/dtos';
import LoadingSpinner from '../components/LoadingSpinner';
import FeedbackDialog, { FeedbackType } from '../components/FeedbackDialog';
import ManualClaimConfigModal from '../components/ManualClaimConfigModal';
import SkeletonLoader from '../components/SkeletonLoader';
import { exportToCsv } from '../utils/csvHelper';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null); // ✅ Use any for now
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [products, setProducts] = useState<ProductSummaryDto[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLogDto[]>([]); // ✅ Dynamic State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(10);
  const [autoRetryActive, setAutoRetryActive] = useState(true);

  // --- CRUD State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentCompany, setCurrentCompany] = useState<Partial<CompanyDto>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [configModalState, setConfigModalState] = useState<{ isOpen: boolean; companyId: string; companyName: string }>({
    isOpen: false,
    companyId: '',
    companyName: ''
  });

  // Feedback Dialog state
  const [feedback, setFeedback] = useState<{
    isOpen: boolean;
    type: FeedbackType;
    title: string;
    message: string;
    primaryActionLabel?: string;
    onPrimaryAction?: () => void;
    secondaryActionLabel?: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  const triggerFeedback = (config: Omit<typeof feedback, 'isOpen'>) => {
    setFeedback({ ...config, isOpen: true });
  };

  // --- Handlers ---
  const openCreateModal = () => {
    setModalMode('create');
    setCurrentCompany({});
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (company: CompanyDto) => {
    setModalMode('edit');
    setCurrentCompany({ ...company }); // Clone to avoid direct mutation
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCompany({});
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Basic Validation
    if (!currentCompany.name || !currentCompany.name.trim()) {
      setFormError("Company Name is required.");
      return;
    }

    try {
      if (modalMode === 'create') {
        const res = await adminApiClient.post<CompanyDto>('/admin/companies', {
          name: currentCompany.name,
          description: currentCompany.description
        });
        setCompanies([...companies, res.data]); // Optimistic update
      } else {
        // Edit Mode
        const res = await adminApiClient.put<CompanyDto>(`/admin/companies/${currentCompany.id}`, {
          name: currentCompany.name,
          description: currentCompany.description
        });

        // Update list
        setCompanies(companies.map((c: CompanyDto) => c.id === currentCompany.id ? res.data : c));
      }
      closeModal();
    } catch (err: any) {
      console.error("Failed to save company:", err);
      setFormError(err.response?.data?.message || "Failed to save company. Please try again.");
    }
  };

  const handleDelete = async (companyId: string) => {
    triggerFeedback({
      type: 'confirm',
      title: 'Delete Company',
      message: 'Are you sure you want to delete this company? This action cannot be undone.',
      primaryActionLabel: 'Yes, Delete',
      onPrimaryAction: async () => {
        try {
          await adminApiClient.delete(`/admin/companies/${companyId}`);
          setCompanies(companies.filter((c: CompanyDto) => c.id !== companyId)); // Remove from list
          triggerFeedback({
            type: 'success',
            title: 'Company Deleted',
            message: 'Company has been deleted successfully.',
          });
        } catch (err: any) {
          console.error("Failed to delete company:", err);
          triggerFeedback({
            type: 'error',
            title: 'Delete Failed',
            message: err.response?.data?.message || 'Failed to delete company. Please try again.',
          });
        }
      },
      secondaryActionLabel: 'Cancel',
    });
  };

  const handleExportCompanies = () => {
    exportToCsv('xtrem_companies_export', companies, {
      id: 'Company ID',
      name: 'Company Name',
      description: 'Description',
      createdAt: 'Registered Date',
      modifiedAt: 'Last Modified'
    });
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch admin profile
      const profileRes = await adminApiClient.get<any>('/admin/profile');
      setUser(profileRes.data);

      // Fetch dashboard stats
      const statsRes = await adminApiClient.get<any>('/admin/dashboard-stats');
      setStats(statsRes.data);

      // Fetch companies
      const companiesRes = await adminApiClient.get<CompanyDto[]>('/admin/companies');
      setCompanies(companiesRes.data);

      // Fetch products
      const productsRes = await adminApiClient.get<ProductSummaryDto[]>('/admin/products');
      setProducts(productsRes.data);

      // Fetch Recent Activity (Audit Logs) - ONLY for Super Admins
      if (profileRes.data.company === 'Xtrem Group') {
        const logsRes = await adminApiClient.get<AuditLogDto[]>('/admin/audit-logs');
        setRecentActivity(logsRes.data);
      }

      setError(null);
    } catch (err: any) {
      console.error('[AdminDashboard] Failed to load data:', err);
      setError(err.response?.data?.message || 'Unable to load data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Reconnection countdown timer
  useEffect(() => {
    if (!error || !autoRetryActive) return;

    if (countdown <= 0) {
      fetchAllData();
      setCountdown(10);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [error, countdown, autoRetryActive]);

  const isSuperAdmin = user?.company === 'Xtrem Group';

  // --- PRD Section 4: Company-Level Dashboards ---
  const displayMetrics = [
    { name: 'Active Customers', value: stats?.activeUsers ?? '0' },
    { name: 'API Usage Today', value: stats?.apiUsageToday ?? '0 req' },
    { name: 'Service Uptime', value: stats?.serviceUptime ?? '0%' },
    { name: 'Pending Claims', value: stats?.pendingClaims ?? '0' },
  ];

  return (
    <>
      {/* PRD Section 4: "Company-Level Dashboards" - Main Header Card (Overview) */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
          <div className="w-full lg:w-auto">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Company Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">Monitor your Xtrem operations</p>
          </div>

          {/* Metrics Grid */}
          {loading ? (
            <SkeletonLoader type="metrics" count={4} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 w-full lg:w-auto">
              {displayMetrics.map((metric: any) => (
                <div key={metric.name} className="flex flex-col lg:items-end">
                  <div className="text-[10px] sm:text-xs text-gray-400 uppercase font-bold tracking-wider">{metric.name}</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900">{metric.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 w-full lg:w-auto sm:justify-end">
            {isSuperAdmin && (
              <button
                onClick={openCreateModal}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Plus size={16} className="mr-1.5" />
                <span className="whitespace-nowrap">New Company</span>
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => navigate('/users')}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center shadow-sm"
                aria-label="Manage users"
              >
                <Users size={16} className="mr-1.5" />
                <span className="whitespace-nowrap">Manage Users</span>
              </button>
            )}
            {isSuperAdmin && companies.length > 0 && (
              <button
                onClick={handleExportCompanies}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center shadow-sm active:scale-95"
                aria-label="Export Companies as CSV"
              >
                <FileText size={16} className="mr-1.5" />
                <span className="whitespace-nowrap">Export CSV</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* PRD Section 4: "Company-Level Dashboards" - Company Metrics */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
        {loading ? (
          <SkeletonLoader type="table" count={5} />
        ) : error ? (
          <div className="p-8 text-center space-y-4 max-w-md mx-auto">
            <div className="mx-auto w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 border border-red-100">
              <AlertCircle size={24} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">Operations Query Failed</h4>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{error}</p>
            </div>
            <div className="flex flex-col items-center gap-2 pt-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setCountdown(10);
                    fetchAllData();
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 active:scale-95"
                >
                  <Activity size={12} className={countdown === 10 ? 'animate-spin' : ''} />
                  <span>Retry Now</span>
                </button>
                <button
                  onClick={() => setAutoRetryActive(!autoRetryActive)}
                  className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold transition-all border border-gray-200"
                >
                  {autoRetryActive ? 'Pause Auto-Retry' : 'Enable Auto-Retry'}
                </button>
              </div>
              {autoRetryActive && (
                <p className="text-[10px] text-gray-400 font-medium">
                  Attempting automatic reconnection in <span className="font-bold text-blue-600">{countdown}s</span>...
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Companies Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Company</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Description</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Registered Date</th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {companies.map((company: CompanyDto) => (
                    <tr key={company.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{company.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{company.description || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(company.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isSuperAdmin && (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(company)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => setConfigModalState({ isOpen: true, companyId: company.id, companyName: company.name })}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Manual Claim Config"
                            >
                              <FileText size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(company.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Companies Card View */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-800">
              {companies.map((company: CompanyDto) => (
                <div key={company.id} className="p-4 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900 dark:text-white">{company.name}</h3>
                    {isSuperAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(company)}
                          className="p-2 text-blue-600 bg-blue-50 dark:bg-blue-950/40 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setConfigModalState({ isOpen: true, companyId: company.id, companyName: company.name })}
                          className="p-2 text-amber-600 bg-amber-50 dark:bg-amber-950/40 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors"
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="p-2 text-red-600 bg-red-50 dark:bg-red-950/40 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-slate-300 mb-3 line-clamp-2">{company.description || 'No description provided.'}</p>
                  <div className="flex items-center text-xs text-gray-400 dark:text-slate-500">
                    <Calendar size={12} className="mr-1" />
                    <span>Registered: {new Date(company.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* PRD Section 8: Audit Logs (Super Admin Only) */}
      {isSuperAdmin && (
        <div className="mt-8 bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Activity className="text-primary-600" size={20} />
              Recent Activity
            </h2>
            <button className="text-sm font-medium text-primary-600 hover:text-primary-700">View All Logs</button>
          </div>
          <div className="bg-white">
            {/* Desktop Audit Logs Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Resource</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">IP Address</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {recentActivity.map((activity: AuditLogDto) => (
                    <tr key={activity.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{activity.user || 'Unknown'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${activity.action.includes('Create') ? 'bg-green-50 text-green-700 border border-green-100' :
                          activity.action.includes('Update') ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                            'bg-gray-50 text-gray-700 border border-gray-100'
                          }`}>
                          {activity.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{activity.resource}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">{new Date(activity.timestamp).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 text-[10px] font-mono">{activity.ipAddress}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Audit Logs Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {recentActivity.map((activity: AuditLogDto) => (
                <div key={activity.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-bold text-gray-900 text-sm">{activity.user || 'Unknown User'}</span>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${activity.action.includes('Create') ? 'bg-green-50 text-green-700' :
                      activity.action.includes('Update') ? 'bg-blue-50 text-blue-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                      {activity.action}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-[9px] text-gray-400 font-bold uppercase">Resource</p>
                      <p className="text-xs text-gray-600 font-medium truncate">{activity.resource}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-gray-400 font-bold uppercase">Timestamp</p>
                      <p className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleTimeString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CRUD Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeModal}></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSave}>
                <div className="bg-white p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                      {modalMode === 'create' ? 'Create New Company' : 'Edit Company'}
                    </h3>
                    <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                      <X size={20} />
                    </button>
                  </div>

                  {formError && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-md">
                      {formError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">Company Name</label>
                      <input
                        type="text"
                        id="name"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                        value={currentCompany.name || ''}
                        onChange={e => setCurrentCompany({ ...currentCompany, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        id="description"
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                        value={currentCompany.description || ''}
                        onChange={e => setCurrentCompany({ ...currentCompany, description: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={feedback.isOpen}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={() => setFeedback({ ...feedback, isOpen: false })}
        primaryActionLabel={feedback.primaryActionLabel}
        onPrimaryAction={feedback.onPrimaryAction}
        secondaryActionLabel={feedback.secondaryActionLabel}
      />

      {configModalState.isOpen && (
        <ManualClaimConfigModal
          companyId={configModalState.companyId}
          companyName={configModalState.companyName}
          onClose={() => setConfigModalState({ isOpen: false, companyId: '', companyName: '' })}
        />
      )}
    </>
  );
}